/**
 * TableComponent is a reusable React component that implements an infinite scrolling and lazy loading
 * data table using PrimeReact's DataTable. It is designed to handle large datasets efficiently by
 * loading data incrementally as the user scrolls.
 *
 * Key Features:
 * - Infinite Scrolling: Loads additional data as the user scrolls to the bottom of the table.
 * - Lazy Loading: Fetches only the required data from the server, reducing the initial load time and
 *   memory usage.
 * - Sorting: Allows columns to be sorted server-side.
 * - Row Selection: Supports selection of single or multiple rows, with an option to select all rows.
 * - Customizable Columns: Allows dynamic column rendering based on the provided configuration.
 * - Gridlines and Striped Rows: Supports visual enhancements like gridlines and striped rows.
 * - Virtual Scrolling: Efficiently renders only the visible rows to improve performance.
 * - Loading Indicator: Displays a loading spinner while fetching data.
 *
 * Props:
 * - tableData: Array of data to be displayed in the table.
 * - setTableData: Function to update the table data.
 * - setSubmissionData: Function to update the submission data.
 * - columns: Array of column configurations.
 * - scrollHeight: Height of the table's scrollable area.
 * - scrollable: Boolean indicating if the table is scrollable.
 * - lazyLoading: Boolean indicating if data is currently being lazily loaded.
 * - setLazyLoading: Function to update the lazy loading state.
 * - loadingTemplate: JSX template to be displayed while data is loading.
 * - tableHeader: Function to render the table header.
 * - isNewData: Boolean indicating if new data is being loaded.
 * - setIsNewData: Function to update the isNewData state.
 * - program_id: ID of the program associated with the table data.
 * - debouncedSearchQuery: Debounced search query string for filtering data.
 * - querySortOrder: Sort order for querying data.
 * - sortField: Field by which data is sorted.
 * - sortOrder: Sort order direction.
 * - handleSort: Function to handle sorting of columns.
 * - stripedRows: Boolean indicating if rows should be striped.
 * - showGridlines: Boolean indicating if gridlines should be shown.
 * - selectedRowData: Array of selected row data.
 * - setSelectedRowData: Function to update the selected row data.
 * - isSelectAllChecked: Boolean indicating if the select all checkbox is checked.
 * - setIsSelectAllChecked: Function to update the select all checkbox state.
 * - expandedRows: Array of expanded row data.
 * - setExpandedRows: Function to update the expanded rows state.
 * - rowExpansionTemplate: JSX template for row expansion.
 * - isServerSide: Boolean indicating if data handling is server-side.
 * - isInfiniteScroll: Boolean indicating if infinite scroll is enabled.
 * - rowClassName: Function to add class names to rows.
 * - selectionMode: Mode of row selection (single/multiple).
 * - metaKeySelection: Boolean indicating if meta key selection is enabled.
 * - onRowClick: Function to handle row click events.
 * - location: Location string used to navigate response data.
 * - api: API endpoint for fetching data.
 * - filters: Object containing filter configurations.
 * - globalFilterFields: Array of fields to be globally filtered.
 */
import log from "loglevel";
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
// import * as constants from "Constants";
import httpService from 'services/httpService.js';
import "primeflex/primeflex.css";
import "primereact/resources/primereact.css";
import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-blue/theme.css";

const TableComponent = ({
  tableData,
  setTableData,
  columns,
  scrollHeight,
  scrollable,
  lazyLoading,
  setLazyLoading,
  loadingTemplate,
  tableHeader,
  isNewData,
  setIsNewData,
  program_id,
  debouncedSearchQuery,
  querySortOrder,
  sortField,
  sortOrder,
  handleSort,
  stripedRows,
  showGridlines,
  selectedRowData,
  setSelectedRowData,
  isSelectAllChecked,
  setIsSelectAllChecked,
  expandedRows,
  setExpandedRows,
  rowExpansionTemplate,
  isServerSide,
  isInfiniteScroll,
  rowClassName: rowClass,
  selectionMode,
  metaKeySelection,
  onRowClick,
  location,
  api,
  filters,
  globalFilterFields,
  numberOfRows = 100,
  size,
  setNextSubmissionURL=() => {},
  isFromAllSubmissions = false
}) => {
  // State to track the clicked row
  const [clickedRow, setClickedRow] = useState([]);
  // State to track the first index of the current page
  const [firstIndex, setFirstIndex] = useState(0);
  // State to track the last index of the current page
  const [lastIndex, setLastIndex] = useState(numberOfRows);
  // Number of rows per page
  const rows = numberOfRows;
  log.log(" inside table component finally", { rows, isNewData })
  useEffect(() => {
    log.log({ isNewData, numberOfRows, rows });
    loadTableDataLazy({ first: firstIndex, last: lastIndex }); // Simulate initial load for the first page
  }, [isNewData]);

  // Function to load table data lazily
  const loadTableDataLazy = async (event) => {
    const { first, last } = event;
    log.log({ first, last, event })
    setFirstIndex(first);
    setLastIndex(last);

    // Calculate the page number based on the last index
    let page = Math.floor((last - 1) / numberOfRows) + 1;

    // Check if the requested data is already loaded
    const isDataLoaded = tableData?.slice?.(first, last)?.every((rowData) => rowData !== undefined);

    // If the data is already loaded and it's not new data, simply return
    if (isDataLoaded && !isNewData) {
      return;
    }

    // Calculate the range of data needed for the current viewport
    const start = (page - 1) * numberOfRows;
    const end = start + numberOfRows;

    // Set lazy loading to true
    setLazyLoading(true);
    // Construct the URL for fetching data
    const url = `${api}?page=${page}&page_size=${rows}&search_query=${debouncedSearchQuery}&sort=${querySortOrder + sortField
      }`;

    try {
      let response;
      // Check if the previous page data is loaded but the current page data is missing
      if (
        tableData[last] !== undefined &&
        tableData[first] === undefined &&
        page !== 1
      ) {
        // Fetch current page data
        const currentResponse = await httpService.get(url);
        const loadedDataCurrent = location
          .split(".")
          .reduce((obj, key) => obj[key], currentResponse);
        // Fetch previous page data
        page = page - 1;
        const previousResponse = await httpService.get(`${url}&page=${page}`);
        const loadedDataPrevious = location
          .split(".")
          .reduce((obj, key) => obj[key], previousResponse);
        const _tableData = [...tableData];
        // Merge previous and current page data into the table data
        Array.prototype.splice.apply(_tableData, [
          start - numberOfRows, // Adjust the start index to include the previous page
          end - start + numberOfRows, // Adjust the length to include the previous page
          ...loadedDataPrevious,
          ...loadedDataCurrent,
        ]);
        setTableData(_tableData);
        // Select all rows if the select all checkbox is checked
        if (isSelectAllChecked) {
          setSelectedRowData(_tableData);
        }
      } else {
        // Fetch only the current page data
        response = await httpService.get(url);
        const loadedData = location
          .split(".")
          .reduce((obj, key) => obj[key], response);
        const _tableData = [...tableData];
        // Merge current page data into the table data
        Array.prototype.splice.apply(_tableData, [
          start, // Adjust the start index to include the previous page
          end - start, // Adjust the length to include the previous page
          ...loadedData,
        ]);
        setTableData(_tableData);
        // Select all rows if the select all checkbox is checked
        if (isSelectAllChecked) {
          setSelectedRowData(_tableData);
        }
        
      }
      if(isFromAllSubmissions) {
          setNextSubmissionURL(response.data.submissions.submissions.next);
        }
      setLazyLoading(false);
    } catch (error) {
      log.log(error);
      setLazyLoading(false);
    }

    setIsNewData(false);
  };

  // Function to render the columns
  const renderColumns = () => {
    return columns.filter(r => !r.hidden).map((column, index) => (
      <Column
        key={index}
        field={column.field}
        header={column.header}
        frozen={column.frozen}
        sortable={column.sortable}
        style={column.style}
        body={column.body}
        expander={column.expander}
        selectionMode={column.selectionMode}
        headerStyle={column.headerStyle}
        hidden={column.hidden}
      />
    ));
  };

  return (
    <DataTable style={{ position: 'relative' }}
      size={size || "normal"}
      // The data to be displayed in the table
      value={tableData}
      // Header of the table, if provided
      header={tableHeader ? tableHeader() : null}
      // Enables lazy loading for server-side data handling
      lazy={isServerSide}
      // Adds class names to rows based on the provided function
      rowClassName={rowClass}
      // Enables scrolling for the table
      scrollable={scrollable}
      // Height of the scrollable area
      scrollHeight={scrollHeight}
      // Allows sorting to be removable
      removableSort
      // Function to handle sorting
      onSort={handleSort}
      // Field by which data is sorted
      sortField={sortField}
      // Sort order direction (ascending/descending)
      sortOrder={sortOrder}
      // Enables striped rows for better readability
      stripedRows={stripedRows}
      // Shows gridlines for better visual separation of rows
      showGridlines={showGridlines}
      // Allows columns to be resized
      resizableColumns
      // Sets the column resize mode
      columnResizeMode="expand"
      // Enables meta key selection for multi-selection
      metaKeySelection={metaKeySelection}
      // Fields to be used for global filtering
      globalFilterFields={globalFilterFields}
      // Function to handle row click events
      onRowClick={onRowClick}
      // Mode of row selection (single/multiple)
      selectionMode={selectionMode}
      // The currently selected row data
      selection={selectedRowData ? selectedRowData : clickedRow}
      // Filter configurations
      filters={filters}
      // Handles row selection changes
      onSelectionChange={(e) =>
        setSelectedRowData
          ? setSelectedRowData(e.value)
          : setClickedRow(e.value)
      }
      // Handles select all rows event
      onAllRowsSelect={(e) => {
        setIsSelectAllChecked(true);
        setTimeout(() => setSelectedRowData(tableData), 300);
      }}
      // Handles unselect all rows event
      onAllRowsUnselect={() => {
        setIsSelectAllChecked(false);
        setSelectedRowData([]);
      }}
      // The currently expanded rows data
      expandedRows={expandedRows}
      // Handles row expansion toggling
      onRowToggle={(e) => setExpandedRows(e.data)}
      // Template for row expansion
      rowExpansionTemplate={rowExpansionTemplate}
      // Configuration for virtual scrolling
      virtualScrollerOptions={{
        // Size of each item
        itemSize: 70,

        // Enables lazy loading for infinite scroll
        lazy: { isInfiniteScroll },

        // Function to load data lazily
        onLazyLoad: loadTableDataLazy,

        // Shows a loader while data is being fetched
        showLoader: true,

        // Loading state
        loading: lazyLoading,

        // Template to be displayed while loading
        loadingTemplate,
      }}
    >
      {/* Renders the columns */}
      {renderColumns()}
    </DataTable>
  );
};

export default TableComponent;
